import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, 
  Users, 
  FileText, 
  BookOpen, 
  Image, 
  Flag, 
  Settings, 
  Shield, 
  LogOut, 
  Menu, 
  X,
  Church,
  ScrollText,
  ChevronDown,
  ChevronRight,
  Bell,
  UserCheck,
  ShoppingBag,
  Package,
  Tags,
  ClipboardList,
  Ticket,
  Warehouse,
  Store,
  Bot,
  IndianRupee
} from 'lucide-react';

export default function AdminLayout({ admin, setAdmin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(location.pathname.includes('/admin/shop'));

  // Shop submenu items
  const shopMenuItems = [
    { icon: Package, label: 'Products', path: '/admin/shop/products' },
    { icon: Tags, label: 'Categories', path: '/admin/shop/categories' },
    { icon: ClipboardList, label: 'Orders', path: '/admin/shop/orders' },
    { icon: Ticket, label: 'Coupons', path: '/admin/shop/coupons' },
    { icon: Warehouse, label: 'Inventory', path: '/admin/shop/inventory' },
    { icon: Settings, label: 'Shop Settings', path: '/admin/shop/settings', roles: ['super_admin'] },
  ];

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Users, label: 'Users', path: '/admin/users' },
    { icon: UserCheck, label: 'Pandits', path: '/admin/pandits' },
    { icon: Shield, label: 'Exam Verification', path: '/admin/pandit-verification' },
    { icon: IndianRupee, label: 'Commission', path: '/admin/commission' },
    { icon: FileText, label: 'Posts', path: '/admin/posts' },
    { icon: Image, label: 'Media', path: '/admin/media' },
    { icon: Flag, label: 'Reports', path: '/admin/reports' },
    { icon: Church, label: 'Temples', path: '/admin/temples' },
    { icon: Bot, label: 'VedVaani AI', path: '/admin/vedvaani', roles: ['super_admin'] },
    { icon: ScrollText, label: 'Audit Logs', path: '/admin/logs', roles: ['super_admin'] },
    { icon: Settings, label: 'Settings', path: '/admin/settings', roles: ['super_admin'] },
    { icon: Shield, label: 'Admins', path: '/admin/admins', roles: ['super_admin'] },
  ];

  const filteredMenu = menuItems.filter(item => 
    !item.roles || item.roles.includes(admin?.role)
  );

  const filteredShopMenu = shopMenuItems.filter(item =>
    !item.roles || item.roles.includes(admin?.role)
  );

  const handleLogout = () => {
    localStorage.removeItem('dharmbook_admin_token');
    localStorage.removeItem('dharmbook_admin');
    setAdmin(null);
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;
  const isShopActive = location.pathname.includes('/admin/shop');

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: 'Super Admin', color: 'bg-red-500' },
      content_admin: { label: 'Content Admin', color: 'bg-blue-500' },
      support_admin: { label: 'Support Admin', color: 'bg-green-500' },
      shop_manager: { label: 'Shop Manager', color: 'bg-amber-500' }
    };
    return badges[role] || { label: role, color: 'bg-gray-500' };
  };

  const badge = getRoleBadge(admin?.role);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-slate-900 border-b border-slate-800 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-slate-400 hover:text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          {/* Desktop sidebar toggle */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:block p-2 text-slate-400 hover:text-white"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold hidden sm:block">DharmBook Admin</span>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Notifications placeholder */}
          <button className="p-2 text-slate-400 hover:text-white relative">
            <Bell className="w-5 h-5" />
          </button>

          {/* Admin info */}
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-white text-sm font-medium">{admin?.name}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full text-white ${badge.color}`}>
                {badge.label}
              </span>
            </div>
            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center text-white font-semibold">
              {admin?.name?.charAt(0) || 'A'}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-16 bottom-0 left-0 bg-slate-900 border-r border-slate-800 z-40 transition-all duration-300 ${
        sidebarOpen ? 'w-64' : 'w-20'
      } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100%-80px)]">
          {/* DharmShop Dropdown - At Top */}
          <div className="mb-2">
            <button
              onClick={() => setShopMenuOpen(!shopMenuOpen)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-all ${
                isShopActive
                  ? 'bg-amber-500/10 text-amber-400'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
              data-testid="nav-dharmshop"
            >
              <div className="flex items-center gap-3">
                <ShoppingBag className={`w-5 h-5 flex-shrink-0 ${isShopActive ? 'text-amber-400' : ''}`} />
                {sidebarOpen && <span className="text-sm font-medium">DharmShop</span>}
              </div>
              {sidebarOpen && (
                <ChevronRight className={`w-4 h-4 transition-transform ${shopMenuOpen ? 'rotate-90' : ''}`} />
              )}
            </button>
            
            {/* Shop Submenu */}
            {shopMenuOpen && sidebarOpen && (
              <div className="ml-4 mt-1 space-y-1 border-l border-slate-700 pl-3">
                {filteredShopMenu.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                        active
                          ? 'bg-amber-500/10 text-amber-400'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`}
                      data-testid={`nav-shop-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <item.icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-amber-400' : ''}`} />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-700 my-3"></div>

          {/* Regular Menu Items */}
          {filteredMenu.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  active
                    ? 'bg-amber-500/10 text-amber-400'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-amber-400' : ''}`} />
                {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all w-full"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`pt-16 transition-all duration-300 ${
        sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
      }`}>
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
